import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router";
import { useTranslation } from "react-i18next";
import { httpsCallable } from "firebase/functions";
import { functions } from "../services/firebase";
import { Globals } from "../globals";
import Container from "react-bootstrap/esm/Container";
import Card from "react-bootstrap/esm/Card";
import Form from "react-bootstrap/esm/Form";
import Button from "react-bootstrap/esm/Button";
import Spinner from "react-bootstrap/esm/Spinner";
import Alert from "react-bootstrap/esm/Alert";
import AppFooter from "../footer/AppFooter";
import { songKey } from "../utils/songTitle";
import { applyGemeindeLanguage } from "../i18n/gemeindeLanguage";
import { findRepeat, formatRepeatDate } from "../utils/repeatWarning";

interface ChoirPlanService {
    serviceId: string;
    date: string; // YYYY-MM-DD
    serviceLabel: string;
    songs: string[];
}

interface ChoirPlanData {
    conductorName: string;
    gemeindeName: string;
    /** The Gemeinde's default language (GOD-50); missing from older functions. */
    gemeindeLanguage?: string;
    songCount: number;
    repeatWarningWeeks: number;
    today: string;
    services: ChoirPlanService[];
    catalogue: { title: string; lastSung: string | null }[];
}

const CATALOGUE_LIST_ID = "choirPlanCatalogue";

/**
 * Public page /chor/:token: the conductor enters the choir songs of the coming
 * services. The token in the link is the only credential – there is no login.
 */
export default function ChoirPlanPage() {
    const { token } = useParams<{ token: string }>();
    const { t } = useTranslation();
    const [plan, setPlan] = useState<ChoirPlanData | null>(null);
    const [state, setState] = useState<"loading" | "ready" | "invalid" | "error">("loading");
    const [openId, setOpenId] = useState<string | null>(null);
    // What is typed for the open service, before saving.
    const [draft, setDraft] = useState<string[]>([]);
    const [saving, setSaving] = useState(false);
    const [savedId, setSavedId] = useState<string | null>(null);
    const [saveError, setSaveError] = useState("");

    const load = useCallback(async () => {
        const result = await httpsCallable<{ token: string }, ChoirPlanData>(functions, "getChoirPlan")({ token: token ?? "" });
        applyGemeindeLanguage(result.data.gemeindeLanguage);
        setPlan(result.data);
        setState("ready");
        return result.data;
    }, [token]);

    useEffect(() => {
        document.title = t("choirPlan.title");
    }, [t]);

    useEffect(() => {
        load()
            .then((data) => {
                // Start where something is missing.
                const first = data.services.find((service) => service.songs.some((song) => !song)) ?? data.services[0];
                if (first) {
                    setOpenId(first.serviceId);
                    setDraft(first.songs);
                }
            })
            .catch((err: { code?: string }) => setState(err.code === "functions/not-found" ? "invalid" : "error"));
    }, [load]);

    const lastSungByKey = useMemo(
        () => new Map((plan?.catalogue ?? []).map((song) => [songKey(song.title), song.lastSung])),
        [plan]
    );

    const formatDate = (isoDate: string) =>
        new Date(`${isoDate}T00:00:00Z`).toLocaleDateString(t("calendar.locale") as string, {
            weekday: "long",
            year: "numeric",
            month: "long",
            day: "numeric",
            timeZone: "UTC",
        });

    /** The hint below a song field: a repeat warning, or that the title is new. */
    const hintFor = (service: ChoirPlanService, title: string): { text: string; warn: boolean } | null => {
        if (!plan || !title.trim()) return null;
        const key = songKey(title);
        // Known dates of the song: its last performance and the other services it is planned for.
        const dates = plan.services
            .filter((other) => other.serviceId !== service.serviceId && other.songs.some((song) => song && songKey(song) === key))
            .map((other) => other.date);
        const lastSung = lastSungByKey.get(key);
        if (lastSung) dates.push(lastSung);
        const repeat = findRepeat(service.date, dates, plan.repeatWarningWeeks);
        if (repeat) {
            return {
                warn: true,
                text: t(repeat.planned ? "choirPlan.repeatPlanned" : "choirPlan.repeatLast", {
                    date: formatRepeatDate(repeat.date, service.date),
                }),
            };
        }
        if (!lastSungByKey.has(key)) return { warn: false, text: t("choirPlan.newSong") };
        return lastSung
            ? { warn: false, text: t("choirPlan.lastSung", { date: formatRepeatDate(lastSung, service.date) }) }
            : null;
    };

    const toggle = (service: ChoirPlanService) => {
        const isOpen = openId === service.serviceId;
        setOpenId(isOpen ? null : service.serviceId);
        setDraft(isOpen ? [] : service.songs);
        setSavedId(null);
        setSaveError("");
    };

    const handleSave = async (e: React.FormEvent, service: ChoirPlanService) => {
        e.preventDefault();
        setSaving(true);
        setSaveError("");
        try {
            await httpsCallable(functions, "submitChoirSongs")({ token, serviceId: service.serviceId, songs: draft });
            const data = await load();
            setDraft(data.services.find((other) => other.serviceId === service.serviceId)?.songs ?? []);
            setSavedId(service.serviceId);
        } catch {
            setSaveError(t("choirPlan.saveFailed") as string);
        } finally {
            setSaving(false);
        }
    };

    if (state === "loading") {
        return (
            <Container className="text-center mt-5">
                <Spinner animation="border" />
                <p className="mt-2">{t("common.loading")}</p>
            </Container>
        );
    }

    if (state !== "ready" || !plan) {
        return (
            <Container className="mt-5" style={{ maxWidth: "600px" }}>
                <Alert variant="danger">
                    <Alert.Heading>{t("myPlan.invalidTitle")}</Alert.Heading>
                    <p>{t(state === "invalid" ? "myPlan.invalidText" : "errors.anErrorOccurred")}</p>
                    <Link to={Globals.PATH_CHOIR_PLAN}>{t("myPlan.requestNewLink")}</Link>
                </Alert>
            </Container>
        );
    }

    return (
        <>
            <Container className="mt-4" style={{ maxWidth: "640px" }}>
                <div className="gl-meta">{plan.gemeindeName}</div>
                <h3 className="mb-1">{t("choirPlan.heading")}</h3>
                <p className="text-muted">{plan.conductorName}</p>

                {plan.services.length === 0 && <Alert variant="info">{t("choirPlan.empty")}</Alert>}

                {plan.services.map((service) => {
                    const isOpen = openId === service.serviceId;
                    const entered = service.songs.filter(Boolean);
                    const badge =
                        entered.length === 0
                            ? { className: "gl-badge--warn", label: t("choirPlan.statusOpen") }
                            : entered.length < plan.songCount
                                ? { className: "gl-badge--accent", label: t("choirPlan.statusPartial", { count: entered.length, total: plan.songCount }) }
                                : { className: "gl-badge--ok", label: t("choirPlan.statusDone") };
                    return (
                        <Card className="mb-3" key={service.serviceId}>
                            <button
                                type="button"
                                className="btn text-start w-100 p-3 border-0"
                                aria-expanded={isOpen}
                                onClick={() => toggle(service)}
                            >
                                <div className="d-flex justify-content-between align-items-start gap-2">
                                    <div>
                                        <div className="fw-semibold">{formatDate(service.date)}</div>
                                        <div className="text-muted">{service.serviceLabel}</div>
                                    </div>
                                    <span className={`gl-badge flex-shrink-0 ${badge.className}`}>{badge.label}</span>
                                </div>
                                {!isOpen && entered.length > 0 && <div className="mt-2 small">{entered.join(" · ")}</div>}
                            </button>
                            {isOpen && (
                                <Card.Body className="border-top">
                                    <Form onSubmit={(e) => handleSave(e, service)}>
                                        {Array.from({ length: plan.songCount }, (_, index) => {
                                            const value = draft[index] ?? "";
                                            const hint = hintFor(service, value);
                                            return (
                                                <Form.Group className="mb-3" controlId={`song-${service.serviceId}-${index}`} key={index}>
                                                    <Form.Label>{t("choirPlan.song", { number: index + 1 })}</Form.Label>
                                                    <Form.Control
                                                        type="text"
                                                        list={CATALOGUE_LIST_ID}
                                                        value={value}
                                                        maxLength={200}
                                                        autoComplete="off"
                                                        placeholder={t("choirPlan.songPlaceholder") as string}
                                                        onChange={(e) => {
                                                            const next = Array.from({ length: plan.songCount }, (_, i) => draft[i] ?? "");
                                                            next[index] = e.target.value;
                                                            setDraft(next);
                                                            setSavedId(null);
                                                        }}
                                                    />
                                                    {hint && (
                                                        <Form.Text className={hint.warn ? "text-danger fw-semibold" : "text-muted"}>
                                                            {hint.text}
                                                        </Form.Text>
                                                    )}
                                                </Form.Group>
                                            );
                                        })}
                                        {saveError && <Alert variant="danger">{saveError}</Alert>}
                                        {savedId === service.serviceId && <Alert variant="success">{t("choirPlan.saved")}</Alert>}
                                        <Button type="submit" disabled={saving} className="w-100">
                                            {saving && <Spinner as="span" animation="border" size="sm" className="me-2" />}
                                            {t("admin.save")}
                                        </Button>
                                    </Form>
                                </Card.Body>
                            )}
                        </Card>
                    );
                })}

                <datalist id={CATALOGUE_LIST_ID}>
                    {plan.catalogue.map((song) => (
                        <option
                            key={song.title}
                            value={song.title}
                            label={song.lastSung ? (t("choirPlan.lastSung", { date: formatRepeatDate(song.lastSung, "") }) as string) : undefined}
                        />
                    ))}
                </datalist>

                <p className="small text-muted mt-4">{t("choirPlan.linkHint")}</p>
            </Container>
            <AppFooter />
        </>
    );
}
